import { applyStyle, easedProgress, sampleStyle, stagePxScale } from "./animationEngine.es6";

const hidden = { opacity: 0, x: -30, y: 10 };
const visible = { opacity: 1, x: 0, y: 0 };
export function wavyStyleAt(time, start, duration, reverse = false, scale = 1) {
  const p = easedProgress({ duration, delay: start, ease: [0.25, 0.46, 0.45, 0.94] }, time);
  return sampleStyle(reverse ? visible : hidden, reverse ? hidden : visible, p, scale);
}

const prepared = new WeakMap();

// Keeps the original text nodes so resetWavy can restore the DOM. Whitespace
// stays as text so lines wrap as before.
function prepare(el) {
  let record = prepared.get(el);
  if (
    record &&
    record.nodes.every(
      ({ anchor, text, value }) => el.contains(anchor) && text.nodeValue === value
    ) &&
    record.chars.every(({ el: char }) => el.contains(char))
  )
    return record;
  if (record) resetWavy(el);
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const texts = [];
  let node;
  while ((node = walker.nextNode())) {
    if (node.parentElement.closest("script, style, textarea")) continue;
    if (node.nodeValue) texts.push(node);
  }
  record = { nodes: [], chars: [], count: 0 };
  texts.forEach((text) => {
    const existing = text.parentElement.closest("[data-aidea-wavy]");
    if (existing) {
      record.chars.push({ el: existing, index: record.count++ });
      return;
    }
    const anchor = document.createComment("wavy");
    const fragment = document.createDocumentFragment();
    fragment.appendChild(anchor);
    const replacements = [];
    text.nodeValue.split("").forEach((letter) => {
      const index = record.count++;
      const child = letter.trim()
        ? document.createElement("span")
        : document.createTextNode(letter);
      if (letter.trim()) {
        child.textContent = letter;
        child.dataset.aideaWavy = "";
        child.style.display = "inline-block";
        record.chars.push({ el: child, index });
      }
      replacements.push(child);
      fragment.appendChild(child);
    });
    text.parentNode.replaceChild(fragment, text);
    record.nodes.push({ anchor, text, value: text.nodeValue, replacements });
  });
  prepared.set(el, record);
  return record;
}

export function applyWavyAt(el, time, duration = 2, delay = 0, reverse = false) {
  const record = prepare(el);
  if (record.observer) record.observer.disconnect();
  const scale = stagePxScale(el);
  record.chars.forEach(({ el: char, index }) => {
    const start = delay + (index * duration * 0.7) / Math.max(1, record.count);
    applyStyle(char, wavyStyleAt(time, start, duration * 0.3, reverse, scale));
  });
  // The editor can re-render the text after a tick; re-apply the frame then.
  if (typeof MutationObserver !== "undefined") {
    record.observer =
      record.observer ||
      new MutationObserver(() => {
        applyWavyAt(el, ...record.sample);
      });
    record.sample = [time, duration, delay, reverse];
    record.observer.observe(el, { childList: true, characterData: true, subtree: true });
  }
}

export function resetWavy(el, preserveNodes = false) {
  const record = prepared.get(el);
  if (!record) return;
  if (preserveNodes) {
    record.chars.forEach(({ el: char }) => {
      char.style.opacity = "";
      char.style.transform = "";
    });
    return;
  }
  if (record.observer) record.observer.disconnect();
  record.nodes.forEach(({ anchor, text, replacements }) => {
    if (anchor.parentNode) anchor.parentNode.replaceChild(text, anchor);
    replacements.forEach((child) => child.remove());
  });
  prepared.delete(el);
}
